import { createFileRoute, Link } from "@tanstack/react-router";
import { ShieldCheck } from "lucide-react";
import { Layout } from "@/components/site/Layout";

export const Route = createFileRoute("/privacy")({
  head: () => ({
    meta: [
      { title: "Privacy Policy | XAU Pro Academy" },
      {
        name: "description",
        content:
          "How XAU Pro Academy collects, uses, and protects your personal data, including authentication via Google and payments via Stripe.",
      },
      { property: "og:title", content: "Privacy Policy | XAU Pro Academy" },
      {
        property: "og:description",
        content:
          "Learn what data XAU Pro Academy collects, how it is used, and the rights you have over your information.",
      },
    ],
    links: [{ rel: "canonical", href: "https://xauproacademy.com/privacy" }],
  }),
  component: PrivacyPage,
});

function PrivacyPage() {
  return (
    <Layout>
      <section className="container mx-auto max-w-3xl px-4 py-16">
        <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-gold-muted text-gold">
          <ShieldCheck className="h-7 w-7" />
        </div>
        <h1 className="mt-6 font-display text-3xl font-bold md:text-4xl">
          Privacy Policy
        </h1>
        <p className="mt-3 text-muted-foreground">
          How we collect, use, and protect your personal information.
        </p>
        <p className="mt-1 text-xs uppercase tracking-wider text-muted-foreground">
          Last Updated: October 2026
        </p>

        <div className="mt-10 space-y-8 text-sm leading-relaxed text-foreground/90">
          <section>
            <h2 className="font-display text-lg font-semibold">
              1. Information We Collect
            </h2>
            <p className="mt-2 text-muted-foreground">
              We collect information you provide directly to us when you create an
              account or use our services, including:
            </p>
            <ul className="mt-3 list-disc space-y-1 pl-5 text-muted-foreground">
              <li>
                <strong className="text-foreground">Account details:</strong> name,
                email address, and profile picture provided through Google OAuth or
                standard email signup.
              </li>
              <li>
                <strong className="text-foreground">Payment metadata:</strong>{" "}
                subscription status, plan, and billing identifiers received from
                Stripe. We never receive or store your full credit-card number.
              </li>
              <li>
                <strong className="text-foreground">Usage data:</strong> basic
                analytics about how you interact with the platform (pages visited,
                lessons completed) used to improve the service.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="font-display text-lg font-semibold">
              2. How We Use Your Data
            </h2>
            <ul className="mt-2 list-disc space-y-1 pl-5 text-muted-foreground">
              <li>To provide access to the Academy and your member dashboard.</li>
              <li>To process payments and manage subscriptions through Stripe.</li>
              <li>
                To send important operational updates regarding your account,
                billing, or service availability.
              </li>
              <li>
                To improve our educational content and platform performance based on
                aggregated usage patterns.
              </li>
            </ul>
            <p className="mt-3 text-muted-foreground">
              We do not use your personal data for third-party advertising.
            </p>
          </section>

          <section>
            <h2 className="font-display text-lg font-semibold">
              3. Data Security
            </h2>
            <p className="mt-2 text-muted-foreground">
              We do not store credit-card information on our servers; all payment
              data is handled directly by Stripe&rsquo;s PCI-DSS-compliant
              infrastructure. Personal data is transmitted over TLS, stored
              encrypted at rest, and protected in our database with row-level
              security policies that restrict access to your own records.
            </p>
          </section>

          <section>
            <h2 className="font-display text-lg font-semibold">
              4. Third-Party Sharing
            </h2>
            <p className="mt-2 text-muted-foreground">
              We do not sell your personal data. We share information only with the
              service providers strictly required to operate the platform:
            </p>
            <ul className="mt-3 list-disc space-y-1 pl-5 text-muted-foreground">
              <li>
                <strong className="text-foreground">Stripe</strong>, our payment
                processor for payments and subscription management.
              </li>
              <li>
                <strong className="text-foreground">Google</strong>,
                authentication when you choose to sign in with Google.
              </li>
              <li>
                <strong className="text-foreground">Managed cloud infrastructure</strong>,
                hosting, database, and serverless functions used to run the
                application.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="font-display text-lg font-semibold">
              5. Data Controller and Legal Basis
            </h2>
            <p className="mt-2 text-muted-foreground">
              The data controller is Orion AI Technologies - F.Z.C, Ajman Free
              Zone, United Arab Emirates. We process personal data in accordance
              with UAE Federal Decree-Law No. 45 of 2021 on the Protection of
              Personal Data and, where applicable to users in the European
              Economic Area or the United Kingdom, the GDPR. Processing relies on
              contract performance, legitimate interest in operating and securing
              the service, legal obligations, and your consent where required.
            </p>
          </section>

          <section>
            <h2 className="font-display text-lg font-semibold">
              6. Data Retention
            </h2>
            <p className="mt-2 text-muted-foreground">
              Account data is kept while your account is active and deleted or
              anonymised within 90 days of account deletion, except billing
              records that must be retained to meet tax and accounting
              obligations. Session activity logs are purged automatically on a
              rolling basis.
            </p>
          </section>

          <section>
            <h2 className="font-display text-lg font-semibold">
              7. Your Rights
            </h2>
            <p className="mt-2 text-muted-foreground">
              You have the right to access, correct, export, or request the
              deletion of your personal data at any time. To exercise any of these
              rights, or to delete your account entirely, contact{" "}
              <a
                href="mailto:support@xauproacademy.com"
                className="text-foreground underline-offset-2 hover:underline"
              >
                support@xauproacademy.com
              </a>
              . We respond to verified requests within 30 days.
            </p>
          </section>

          <section>
            <h2 className="font-display text-lg font-semibold">
              8. Age Restriction
            </h2>
            <p className="mt-2 text-muted-foreground">
              The platform and its subscriptions are intended only for persons
              aged 18 or older. We do not knowingly collect data from minors and
              will delete any such data brought to our attention.
            </p>
            <p className="mt-3 text-muted-foreground">
              For information on the risks associated with using our educational
              content, see our{" "}
              <Link to="/risk-disclaimer" className="text-foreground underline-offset-2 hover:underline">
                Risk Disclaimer
              </Link>
              .
            </p>
          </section>
        </div>
      </section>
    </Layout>
  );
}
